import { Alert, AlertColor, Box, CircularProgress } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar/UPSnackbar';
import UPButton from 'components/button';
import UPEditActionIcon from 'components/edit-action-icon';
import UPEmptyState from 'components/empty-state';
import ErrorBar from 'components/error-bar';
import { UPTanStackTable } from 'components/tanstack-table';
import { useGridPagination } from 'hooks/useGridPagination';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DATAGRID__NO_ROWS,
  FEATURE_ACCESS__ACCESS_GRANTED,
  FEATURE_ACCESS__ACCESS_REMOVED,
  FEATURE_ACCESS__BULK_BLOCKED,
  FEATURE_ACCESS__BULK_SAVED,
  FEATURE_ACCESS__COMPANY_NOT_FOUND,
  FEATURE_ACCESS__EDIT_ALL,
  FEATURE_ACCESS__EMPTY_STATE,
  FEATURE_ACCESS__SELECTED,
  GENERAL__CLEAR_ALL,
  GENERAL__RETRY,
} from 'translations/constants';
import {
  IBulkFeatureAccessCompanyResult,
  ICompanyFeatureAccess,
  IFeatureState,
} from 'types/feature-access/IFeatureAccess';
import { buildFeatureAccessColumns } from './columns';
import {
  INITIAL_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  TABLE_HEADER_HEIGHT_PX,
  TABLE_ROW_HEIGHT_PX,
  TABLE_ROW_SPACING_PX,
} from './constants';
import FeatureAccessBulkPanel from './FeatureAccessBulkPanel';
import FeatureAccessPanel from './FeatureAccessPanel';
import FeatureAccessToolbar from './FeatureAccessToolbar';
import { BulkApplied, useFeatureAccessBulk } from './hooks/useFeatureAccessBulk';
import { useFeatureAccessOverview } from './hooks/useFeatureAccessOverview';
import { useFeatureAccessPanel } from './hooks/useFeatureAccessPanel';
import { FeatureAccessBody, FeatureAccessContainer } from './styles';
import {
  FeatureFilter,
  filterFeatureAccessRows,
  getBlockedCompanies,
  IFeatureAccessRow,
  isAllSelected,
  pruneSelection,
  SaveOutcome,
  StatusFilter,
  toggleSelectAll,
} from './utils';

const getRowId = (row: IFeatureAccessRow) => row.companyId;
const getRowTestId = (row: IFeatureAccessRow) => `feature-access-row-${row.companyId}`;

interface Toast {
  id: number;
  type: AlertColor;
  content: string;
}

const FeatureAccess = (): JSX.Element => {
  const [t] = useTranslation();
  const overview = useFeatureAccessOverview();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToasts = useCallback((next: Omit<Toast, 'id'>[]) => {
    setToasts(previous => [...previous, ...next.map((toast, index) => ({ ...toast, id: Date.now() + index }))]);
  }, []);
  const dismissToast = (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id));

  const { reload } = overview;
  const panel = useFeatureAccessPanel({
    onSaved: (company: ICompanyFeatureAccess, outcome: SaveOutcome) => {
      reload();
      const next: Omit<Toast, 'id'>[] = [];
      if (outcome.granted) {
        next.push({ type: 'success', content: t(FEATURE_ACCESS__ACCESS_GRANTED, { company: company.companyName }) });
      }
      if (outcome.removed) {
        next.push({ type: 'success', content: t(FEATURE_ACCESS__ACCESS_REMOVED, { company: company.companyName }) });
      }
      pushToasts(next);
    },
    onClosed: () => reload(),
    onNotFound: () => {
      reload();
      pushToasts([{ type: 'error', content: t(FEATURE_ACCESS__COMPANY_NOT_FOUND) }]);
    },
    onStale: () => reload(),
    onError: (messageKey: string) => pushToasts([{ type: 'error', content: t(messageKey) }]),
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [feature, setFeature] = useState<FeatureFilter>('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const filteredRows = useMemo(
    () => filterFeatureAccessRows(overview.rows, searchTerm, feature, status),
    [overview.rows, searchTerm, feature, status],
  );
  const { pagination, setPagination, resetToFirstPage } = useGridPagination(filteredRows, getRowId, INITIAL_PAGE_SIZE);

  // Whatever the filter or a reload hides must not stay selected: "Edit all" would otherwise change
  // Companies the user can no longer see.
  useEffect(() => {
    setSelectedIds(previous => pruneSelection(previous, filteredRows));
  }, [filteredRows]);

  const onSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );
  const onFeatureChange = useCallback(
    (value: FeatureFilter) => {
      setFeature(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );
  const onStatusChange = useCallback(
    (value: StatusFilter) => {
      setStatus(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const allSelected = isAllSelected(selectedIds, filteredRows);
  const tableRowSlots = Math.min(pagination.pageSize, filteredRows.length);
  const tableMinHeight =
    TABLE_HEADER_HEIGHT_PX + tableRowSlots * TABLE_ROW_HEIGHT_PX + (tableRowSlots + 2) * TABLE_ROW_SPACING_PX;
  const selectedCompanyNames = useMemo(
    () => overview.rows.filter(row => selectedIds.includes(row.companyId)).map(row => row.companyName),
    [overview.rows, selectedIds],
  );
  const onToggleSelectAll = useCallback(
    () => setSelectedIds(previous => toggleSelectAll(previous, filteredRows)),
    [filteredRows],
  );

  const [blocked, setBlocked] = useState<{
    companies: IBulkFeatureAccessCompanyResult[];
    changes: IFeatureState[];
  } | null>(null);

  const bulk = useFeatureAccessBulk({
    onApplied: ({ response, changes }: BulkApplied) => {
      reload();
      setSelectedIds([]);
      const blockedCompanies = getBlockedCompanies(response);
      setBlocked(blockedCompanies.length > 0 ? { companies: blockedCompanies, changes } : null);
      // A company counts as saved unless it was blocked with nothing else applied.
      const saved = response.companies.filter(c => c.blocked.length === 0 || c.applied.length > 0).length;
      if (saved > 0) pushToasts([{ type: 'success', content: t(FEATURE_ACCESS__BULK_SAVED, { total: saved }) }]);
    },
    onNotFound: () => {
      reload();
      pushToasts([{ type: 'error', content: t(FEATURE_ACCESS__COMPANY_NOT_FOUND) }]);
    },
    onStale: () => reload(),
    onError: (messageKey: string) => pushToasts([{ type: 'error', content: t(messageKey) }]),
  });

  const { open: openPanel } = panel;
  const onEdit = useCallback((row: IFeatureAccessRow) => openPanel(row), [openPanel]);
  const columns = useMemo(() => buildFeatureAccessColumns(t, onEdit), [t, onEdit]);

  const renderBody = () => {
    if (overview.loadErrorKey) {
      return (
        <Box
          data-testid="feature-access-load-error"
          sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}
        >
          <ErrorBar message={t(overview.loadErrorKey)} />
          <UPButton text={t(GENERAL__RETRY)} onClick={overview.reload} outlined testId="feature-access-load-retry" />
        </Box>
      );
    }
    if (!overview.hasLoaded) return <CircularProgress data-testid="feature-access-loading" />;
    if (overview.rows.length === 0) {
      return <UPEmptyState messageKey={FEATURE_ACCESS__EMPTY_STATE} testId="feature-access-empty-state" />;
    }
    return (
      <>
        <FeatureAccessToolbar
          onSearch={onSearch}
          feature={feature}
          onFeatureChange={onFeatureChange}
          status={status}
          onStatusChange={onStatusChange}
        />
        <UPTanStackTable
          sx={{
            '& .MuiTableContainer-root': { maxHeight: 'none', minHeight: `${tableMinHeight}px`, overflow: 'visible' },
          }}
          data={filteredRows}
          columns={columns}
          getRowId={getRowId}
          getRowTestId={getRowTestId}
          testId="feature-access-table"
          emptyMessage={t(DATAGRID__NO_ROWS)}
          pagination={pagination}
          onPaginationChange={setPagination}
          autoResetPageIndex={false}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          enableRowSelection
          selectedRows={selectedIds}
          onSelectionChange={ids => setSelectedIds(ids as number[])}
          customHeaderCheckbox={{
            checked: allSelected,
            indeterminate: selectedIds.length > 0 && !allSelected,
            onChange: onToggleSelectAll,
            testId: 'feature-access-select-all',
          }}
          footerActions={[
            {
              id: 'edit-all',
              label: t(FEATURE_ACCESS__EDIT_ALL),
              icon: <UPEditActionIcon />,
              onClick: bulk.open,
              testId: 'feature-access-edit-all',
            },
          ]}
          footerCanClearAll
          footerClearAllText={t(GENERAL__CLEAR_ALL)}
          footerSelectedText={t(FEATURE_ACCESS__SELECTED)}
          footerSelectedCount={selectedIds.length}
          footerSelectedCountTestId="feature-access-selected-count"
          footerTotalCount={filteredRows.length}
        />
      </>
    );
  };

  return (
    <FeatureAccessBody>
      <FeatureAccessContainer>
        {blocked && (
          <Alert
            severity="error"
            data-testid="feature-access-bulk-blocked"
            onClose={() => setBlocked(null)}
            action={
              <UPButton
                text={t(GENERAL__RETRY)}
                outlined
                disabled={bulk.isSaving}
                testId="feature-access-bulk-blocked-retry"
                onClick={() =>
                  bulk.retry(
                    blocked.companies.map(c => c.companyId),
                    blocked.changes,
                  )
                }
              />
            }
          >
            {/* The names are appended here, not interpolated into the translation, so they are listed even when the
              translation value is missing or leaves out a placeholder (same as EditTimeRegistrationSettings). */}
            {t(FEATURE_ACCESS__BULK_BLOCKED)}: {blocked.companies.map(c => c.companyName).join(', ')}
          </Alert>
        )}
        {renderBody()}
        <FeatureAccessPanel panel={panel} />
        <FeatureAccessBulkPanel bulk={bulk} selectedIds={selectedIds} selectedCompanyNames={selectedCompanyNames} />
        {toasts.map((toast, index) => (
          <UPSnackBar
            key={toast.id}
            open
            type={toast.type}
            content={toast.content}
            onClose={() => dismissToast(toast.id)}
            vertical="bottom"
            horizontal="right"
            index={index}
          />
        ))}
      </FeatureAccessContainer>
    </FeatureAccessBody>
  );
};

export default FeatureAccess;
